import { observable, action, computed, autorun } from 'mobx'

import { setter }                                from 'mobx-decorators'
import DateFormat                                from 'helpers/DateFormat'
import uiStore                                   from 'stores/UiStore'
import districtStore                             from 'stores/DistrictStore'
import dayjs                                     from 'dayjs'
import dayjsUTC                                  from 'dayjs/plugin/utc'
import dayjsTimezone                             from 'dayjs/plugin/timezone'
import _                                         from 'lodash'

dayjs.extend(dayjsUTC)
dayjs.extend(dayjsTimezone)

export const SCHEDULED_STATUS = 'scheduled'
export const CANCELED_STATUS  = 'canceled'
export const SENDING_STATUS   = 'sending'
export const FAILED_STATUS    = 'failed'

export default class Broadcast {
  broadcastStore = null
  @observable id = null
  @observable senderName = null
  @setter @observable contacts = null
  @observable createdAt = null
  @observable runAt = null
  @observable status = null
  @observable students = null
  @observable studentsCount = null
  @observable nameArray = []
  @observable contactsCount = null
  @setter @observable body = null
  @setter @observable recipients = null
  @setter @observable isNew = false
  @setter @observable action = null
  @setter @observable isLoading = false
  @setter @observable isTranslating = false
  @setter @observable isError = false
  @setter @observable isLoadingContacts = false

  constructor(store, json) {
    this.broadcastStore = store
    this.update(json)
    this.initAutoruns()
  }

  initAutoruns = () => {
    this.autoErrorNotifier()
  }

  autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    'error'
        })
      }
    })
  }

  @computed get fullDate() {
    return `${DateFormat.fullDate(
      this.createdAt
    )} at ${DateFormat.time12Hour(this.createdAt)}`
  }

  @computed get fullDateWithTime() {
    return DateFormat.fullDateWithTime(this.listedAt)
  }

  @computed get createdDateWithTime() {
    return DateFormat.fullDateWithTime(this.createdAt)
  }

  // Scheduled, and has since gone out (or tried to). Immediate sends have no run_at.
  @computed get wasScheduled() {
    return !!this.runAt && !this.isScheduled && !this.isCanceled
  }

  // Send time once a scheduled broadcast has gone out, created time otherwise --
  // mirrors turbine's sort=sent_at so the row's position and timestamp agree.
  @computed get listedAt() {
    return this.wasScheduled ? this.runAt : this.createdAt
  }

  // Failed rows have no badge yet, so at least don't label them sent.
  @computed get deliveryLabel() {
    if (this.status === FAILED_STATUS)  return 'Failed'
    if (this.status === SENDING_STATUS) return 'Sending'

    return 'Sent'
  }

  // Parsed, not string-compared: the serialized offset changes across DST.
  @computed get listedAtTime() {
    return dayjs(this.listedAt).valueOf()
  }

  @computed get fullDateWithScheduledTime() {
    return this.scheduledAt && this.scheduledAt.format('ddd MMM D YYYY HH:mm:ss')
  }

  // A sent broadcast keeps its run_at, so read status rather than run_at.
  @computed get isScheduled() {
    return this.status === SCHEDULED_STATUS
  }

  @computed get isCanceled() {
    return this.status === CANCELED_STATUS
  }

  // District's zone, not the browser's -- a late send would show the wrong day.
  @computed get scheduledAt() {
    if (!this.runAt) return null

    const zone = districtStore.timeZoneName

    return zone ? dayjs(this.runAt).tz(zone) : dayjs(this.runAt)
  }

  @computed get deliveryDate() {
    return this.scheduledAt && this.scheduledAt.format('ddd MMM D')
  }

  // Groups and classes by name; individually-picked students and contacts as counts.
  @computed get audienceSummary() {
    const byType   = _.groupBy(this.recipients, 'type')
    const groups   = (byType.group  || []).map((r) => _.get(r, 'group.group_name',   'Unknown Group'))
    const courses  = (byType.course || []).map((r) => _.get(r, 'course.course_name', 'Unknown Class'))
    const students = (byType.student  || []).length
    const contacts = (byType.contact  || []).length

    const parts = [...groups, ...courses]

    if (students) parts.push(`${students} ${students === 1 ? 'student' : 'students'}`)
    if (contacts) parts.push(`${contacts} ${contacts === 1 ? 'contact' : 'contacts'}`)

    return parts.join(', ')
  }

  @computed get recipientName() {
    if (this.nameArray.length === 1) return this.nameArray[0]
    if (this.nameArray.length === 2)
      return `${this.nameArray[0]} and ${this.nameArray[1]}`
    if (this.nameArray.length === 3) {
      return `${this.nameArray[0]}, ${this.nameArray[1]}, and ${
        this.nameArray[2]
      }`
    }
    return 'Unknown Contacts'
  }

  @computed get isSelected() {
    return this.broadcastStore.selectedBroadcast === this
  }

  @action getRecipientName = () => {
    // update() reruns on every refetch; start over rather than appending.
    this.nameArray.clear()

    const recipientsByType = _.groupBy(this.recipients, 'type')

    if (!_.isEmpty(recipientsByType.group)) {
      if (recipientsByType.group.length === 1) {
        this.nameArray.push(
          _.get(
            recipientsByType,
            'group[0].group.group_name',
            'Unknown Group'
          )
        )
      } else {
        this.nameArray.push(`${recipientsByType.group.length} groups`)
      }
    }

    if (!_.isEmpty(recipientsByType.course)) {
      if (recipientsByType.course.length === 1) {
        this.nameArray.push(
          _.get(
            recipientsByType,
            'course[0].course.course_name',
            'Unknown Class'
          )
        )
      } else {
        this.nameArray.push(
          `${recipientsByType.course.length} classes`
        )
      }
    }

    if (!_.isEmpty(recipientsByType.contact)) {
      if (recipientsByType.contact.length === 1) {
        this.nameArray.push(
          _.get(recipientsByType, 'contact[0].contact.name', '')
        )
      } else {
        this.nameArray.push(
          `${recipientsByType.contact.length} contacts`
        )
      }
    }
  }

  @action handleSelect = () => {
    this.broadcastStore.setSelectedBroadcast(this)

    // Scheduled has no roster yet; its selection is already in the list payload.
    if (!this.isScheduled) this.broadcastStore.fetchBroadcastContacts(this)

    uiStore.setShowBroadcastInfo(true)
  }

  @action update = ({
    id,
    user,
    body,
    contacts,
    students,
    isNew,
    status,
    user_id: userId,
    run_at: runAt,
    created_at: createdAt,
    recipients: recipients,
    contacts_count: contactsCount,
    students_count: studentsCount
  }) => {
    this.id = id
    this.body = body
    // List payloads omit the lazily fetched roster; keep one already loaded.
    if (contacts !== undefined) this.contacts = contacts
    this.isNew = isNew
    this.students = students
    this.senderName = user.full_name
    this.userId = userId
    this.status = status
    this.runAt = runAt
    this.createdAt = createdAt
    this.recipients = recipients
    this.contactsCount = contactsCount
    this.studentsCount = studentsCount
    this.getRecipientName()
  }
}
